import { Pagination, Typography } from '@mui/material'
import { observer } from 'mobx-react-lite'
import { AppBox } from 'src/component/common/analog/molecule/AppBox'
import { useEffect, useState, useRef, useCallback, useMemo } from 'react'
import { AppPaper } from 'src/component/common/analog/molecule/AppPaper'
import { useMyTeamStore } from '../../store/my-team.store'
import { TeamUnusedSkillGapBox } from './TeamUnusedSkillGapBox'
import { Caption } from 'src/feature/localization/component/atom/Caption'
import { AppSkeleton } from 'src/component/common/analog/molecule/AppSkelton'
import { useLocalizationStore } from 'src/feature/localization/store/localization.store'
import { SkillsSearchBar } from './SkillsSearchBar'
import { Palette } from 'src/feature/theme/model/Palette'
import ArrowDownwardRoundedIcon from '@mui/icons-material/ArrowDownwardRounded'
import CheckRoundedIcon from '@mui/icons-material/CheckRounded'
import ArrowUpwardRoundedIcon from '@mui/icons-material/ArrowUpwardRounded'

export const TeamUnusedSkills = observer(() => {
  const myTeamStore = useMyTeamStore()
  const { translation } = useLocalizationStore()
  const { isLoading } = myTeamStore.getGenericFieldsState('getAllUnusedSkills')
  
  const [showGap, setShowGap] = useState<boolean>(false)
  const [pagelength, setPageLength] = useState<number>(0)
  const [searchText, setSearchText] = useState<string>('')
  const prevSearchTextRef = useRef<string>('')
  const pageSize = 10
  const pageTranslation = translation.pages.myTeam

  const legendItems = useMemo(() => [
    { color: Palette.DANGER, label: 'Under 2.0', icon: <ArrowDownwardRoundedIcon sx={{ fontSize: 14 }} /> },
    { color: Palette.GOLDEN_ORANGE, label: '2.0 - 3.9', icon: <CheckRoundedIcon sx={{ fontSize: 14 }} /> },
    { color: Palette.OLIVE_GREEN, label: '4.0+', icon: <ArrowUpwardRoundedIcon sx={{ fontSize: 14 }} /> },
  ], [])

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Check if search text has actually changed and we're not on the first page
        if (prevSearchTextRef.current !== searchText && pagelength !== 0) {
          setPageLength(0)
          prevSearchTextRef.current = searchText
          
          return // Don't fetch data yet, let the pagelength change trigger the fetch
        }
        
        // Update the ref to track current search text
        prevSearchTextRef.current = searchText

        await myTeamStore.getAllUnusedSkills(pagelength, pageSize, searchText)
        setShowGap(true)
      } catch (error) {
        setShowGap(false)
      }
    }
    fetchData()
  }, [searchText, pagelength, myTeamStore])

  const handlePageChange = useCallback((_: React.ChangeEvent<unknown>, page: number) => {
    const newPageLength = (page - 1) * pageSize
    setPageLength(newPageLength)
  }, [pageSize])

  return (
    <AppPaper sx={{ padding: '10px 15px', borderRadius: '10px', gap: 20, width: '100%', minHeight: 500 }}>
      <AppBox sx={{ width: '100%', flexDirection: { xs: 'column', md: 'row' }, justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <AppBox sx={{ flexDirection: 'column', gap: 3 }}>
          <Typography variant='h4'>{pageTranslation.metricsHeader.unusedSkills()}</Typography>
          <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>Show all unused skills</Typography>
          <AppBox sx={{ flexDirection: 'row', gap: 5, alignItems: 'center' }}>
            {legendItems.map(({ color, label, icon }) => (
              <AppBox key={label} sx={{ gap: 5, alignItems: 'center', justifyContent: 'center' }}>
                <AppBox sx={{ ...styles.iconBox, color }}>{icon}</AppBox>
                <Typography sx={{ fontSize: 11 }}>{label}</Typography>
              </AppBox>
            ))}
          </AppBox>
        </AppBox>
        <AppBox sx={{ width: { xs: '100%', md: 'auto' } }}>
          <SkillsSearchBar
            count={myTeamStore.allUnusedSkills?.count || '0'}
            value={searchText}
            onDebouncedChange={setSearchText}
            debounceDelay={1500}
          />
        </AppBox>
      </AppBox>
      <AppBox sx={{ width: '100%', flexDirection: 'column', gap: 10, flex: 1, justifyContent: !isLoading && showGap && myTeamStore.allUnusedSkills?.count === 0 ? 'center' : 'flex-start', alignItems: !isLoading && showGap && myTeamStore.allUnusedSkills?.count === 0 ? 'center' : 'stretch' }}>
        {!isLoading && showGap && myTeamStore.allUnusedSkills?.count !== 0 && myTeamStore.allUnusedSkills?.skillSets.map(skill => (
          <TeamUnusedSkillGapBox key={skill.ids[0]} skillSet={skill} />
        ))}
        {isLoading && (
          <AppSkeleton
            count={pageSize}
            direction="column"
            rects={[
              { width: '100%', height: 25, color: 'background.dark' },
            ]}
          />
        )}
        {!isLoading && showGap && myTeamStore.allUnusedSkills?.count === 0 && <Caption sx={{ color: 'text.secondary', fontSize: 11 }}>{translation.noData()}</Caption>}
      </AppBox>
      {(myTeamStore.allUnusedSkills?.count !== 0 && myTeamStore.allUnusedSkills?.count > 15) && <AppBox alignItems='center' justifyContent='center' width='100%'>
        <Pagination
          shape='rounded'
          color='primary'
          count={Math.ceil((myTeamStore.allUnusedSkills?.count || 0) / pageSize)}
          page={Math.floor(pagelength / pageSize) + 1}
          showFirstButton
          showLastButton
          onChange={handlePageChange}
          sx={{ '& .MuiPaginationItem-root': { fontSize: 12, height: 20 } }}
        />
      </AppBox>}
    </AppPaper>
  )
})

const styles = {
  iconBox: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
}
